import { AccessRecord } from "common/records/Access";
import { UserRecord } from "common/records/User";
import { RootState } from "common/reducers";
import { currentUser, user } from "common/selectors/user.common.selectors";
import { Nullable } from "common/types";
import { List, Map } from "immutable";
import { createSelector } from "reselect";
import { ids } from "common/selectors/idsManagement.selectors";
import { currentOrgId } from "common/selectors/org.common.selectors";

const rootKey = "accesses";

/**
 * Loads the accesses byId
 *
 * @param state The RootState
 * @returns {Map<number, AccessRecord>} byId
 */
export const byId = (state: RootState) => state.getIn([rootKey, "byId"]);

/**
 * Loads the accesses from lastSavedByID
 *
 * @param {RootState} state The RootState
 * @param {{}} options
 * @returns {Map<number, AccessRecord>} lastSavedById
 */
export const lastSavedById = (state: RootState) => state.getIn([rootKey, "lastSavedById"]);

/**
 * Get the access by accessId
 *
 * @param {RootState} state The RootState
 * @param {{accessId: number}} options
 * @returns {AccessRecord} access
 */
export const accessById = (state: RootState, { accessId }: { accessId: number }) => byId(state).get(accessId);

/**
 * Get the accesses ids from idsManagement
 *
 * @param {RootState} state The RootState
 * @returns {List<number>} List<number>
 */
export const accessIds = (state: RootState) => ids(state, { modelName: rootKey }) as List<number>;

export const accessIdsWithoutCurrent = createSelector(
  [accessIds, currentUser, byId],
  (accessIds: List<number>, user: Nullable<UserRecord>, byId: Map<number, AccessRecord>) => {
    if (!user) {
      return accessIds;
    }
    return accessIds.filter((id) => {
      return byId.get(id)?.uid !== user.current_access_uid;
    });
  },
);

/**
 * Returns a list of all the user's accesses
 *
 * @param {RootState} state The RootState
 * @param {{userId: number}} options
 * @returns {List<AccessRecord>} accesses
 */
export const userAccesses = createSelector([user, byId], (user, accessesById): List<AccessRecord> => {
  if (!user) {
    return List();
  }

  return user
    .get("accesses", List())
    .map((accessId: number) => accessesById.get(accessId))
    .filter((access) => !!access) as List<AccessRecord>;
});

/**
 * hydrates a users w/ any access that have been changed (are dirty).
 * For usage when saving back to the server and it's needed in 1 object.
 *
 * @param {RootState} state The RootState
 * @param {{userId: number}} options
 * @returns {UserRecord} user
 */
export const dirtyUserAccesses = createSelector(
  [user, byId, lastSavedById],
  (
    user: Nullable<UserRecord>,
    accessesById: Map<number, AccessRecord>,
    lastSavedById: Map<number, AccessRecord>,
  ): List<AccessRecord> => {
    if (!user || !user.accesses || user.accesses.size === 0) {
      return List();
    }
    return user.accesses
      .map((accessId: number) => {
        const access = accessesById.get(accessId);
        const lastSaved = lastSavedById.get(accessId);

        if (access === undefined || (accessId > 0 && access.equals(lastSaved))) {
          return null;
        }
        return access;
      })
      .filter((a: AccessRecord | null) => a !== null) as List<AccessRecord>;
  },
);

const template = createSelector(
  [byId, (_, props) => props],
  (byId: Map<number, AccessRecord>, { accessId }: { accessId: number }) => {
    return byId.get(accessId)?.template;
  },
);

/**
 * Checks to see if template on access is custom
 *
 * @param {RootState} state The RootState
 * @param {{accessId: number}} options
 * @returns {boolean} boolean
 */
export const isCustomTemplate = createSelector([template, (_, props) => props], (template: string | undefined) => {
  return template === "custom";
});

/**
 * Checks to see if a user is active in the current org, should return true if data is not loaded yet
 *
 * @param {RootState} state The RootState
 * @param {{orgId: number, userId: number}} options
 * @returns {boolean} boolean
 */
export const isUserActive = (state: RootState, { userId }: { userId: Nullable<number> }) => {
  const orgId = currentOrgId(state);
  if (!orgId) {
    return true;
  }
  if (!userId) {
    return true;
  }

  const accessId = state.getIn(["accesses", "batchCheckByOrgUserId", orgId, userId]);

  if (accessId === null) {
    return false;
  }

  return true;
};
